import { Box, Group, Paper, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import { PiDownloadSimple, PiPaperPlaneTilt } from 'react-icons/pi';
import { ActivityCard } from '@/components/InvoiceDetailsModal/ActivityCard';
import { ChainStrip } from '@/components/InvoiceDetailsModal/ChainStrip';
import { ActionButton } from '@/components/InvoiceDetailsModal/DocumentCard';
import { DocumentHeader } from '@/components/InvoiceDetailsModal/DocumentHeader';
import {
  DocumentLayout,
  DocumentLoading,
} from '@/components/InvoiceDetailsModal/DocumentLayout';
import { DocumentView } from '@/components/InvoiceDetailsModal/DocumentView';
import { finalInvoiceActions } from '@/components/InvoiceDetailsModal/documentActions';
import { OfferLine } from '@/components/InvoiceDetailsModal/OfferLine';
import { OpportunityButton } from '@/components/InvoiceDetailsModal/OpportunityButton';
import { PartiesCard } from '@/components/InvoiceDetailsModal/PartiesCard';
import { FinalPaymentCard } from '@/components/InvoiceDetailsModal/PaymentCard';
import { useFollowDocument } from '@/components/InvoiceDetailsModal/ProformaDocument';
import { holds } from '@/lib/permissionMatch';
import {
  useDocumentGroup,
  useDownloadFinalInvoicePDF,
  useFinalInvoiceDetails,
  useSendFinalInvoice,
} from '@/models/invoices';
import { usePermissions } from '@/models/permissions';

const day = (iso: string) => iso.slice(0, 10);

const finalLastSent = (
  deliveries: { sentAt: string; recipientEmail: string; succeeded: boolean }[],
) => {
  const last = deliveries
    .filter((d) => d.succeeded)
    .sort((a, b) => b.sentAt.localeCompare(a.sentAt))[0];
  return last
    ? `Last sent ${dayjs(last.sentAt).format('DD.MM.YYYY HH:mm')} to ${last.recipientEmail}`
    : null;
};

/**
 * The final invoice page: the same layout as the proforma's; it has one immutable version, so
 * no Versions card, and its money facts are its proforma's.
 */
export function FinalInvoiceDocument({ id }: { id: number }) {
  const q = useFinalInvoiceDetails(id);
  const group = useDocumentGroup('final_invoice', id);
  const download = useDownloadFinalInvoicePDF();
  const send = useSendFinalInvoice();
  const { permissions } = usePermissions();
  useFollowDocument(q.dataUpdatedAt, id, q.isPlaceholderData);
  const f = q.data;

  if (q.isLoading) {
    return <DocumentLoading />;
  }
  if (!f) {
    return (
      <Text c="dimmed" p="md">
        Invoice not found, or you may not open it.
      </Text>
    );
  }

  const queued = f.queuedSends.some(
    (s) =>
      s.recipientEmail.toLowerCase() === (f.deliveryEmail ?? '').toLowerCase(),
  );
  const sendAction = holds(permissions, 'invoices:send')
    ? finalInvoiceActions(
        {
          sendBlockedReason: f.sendBlockedReason,
          deliveryEmail: f.deliveryEmail,
          queued,
        },
        permissions,
      )
        .flatMap((g) => g.actions)
        .find((a) => a.key === 'send')
    : undefined;

  const doSend = () =>
    send.mutate(
      { id: f.id },
      {
        onSuccess: (r) =>
          notifications.show({
            color: r.resent ? 'green' : 'orange',
            title: r.resent ? `Sent to ${r.resentTo}` : 'Queued',
            message: r.note ?? r.error ?? `Invoice ${f.number} was sent.`,
          }),
        onError: (e) =>
          notifications.show({
            color: 'red',
            title: 'Not sent',
            message: e instanceof Error ? e.message : String(e),
          }),
      },
    );
  const doDownload = () =>
    download.mutate(f.id, {
      onError: (e) =>
        notifications.show({
          color: 'orange',
          title: 'Not downloaded',
          message: e instanceof Error ? e.message : String(e),
        }),
    });

  const warnings = [
    f.partyDiffersFromProforma
      ? `The proforma was billed to ${f.proforma.partyName}.`
      : null,
    f.senderWarning,
    f.vatWarning
      ? `VAT for this party would be ${(f.vatWarning.partyRate * 100).toFixed(2)}%; the proforma charged ${(f.vatWarning.proformaRate * 100).toFixed(2)}%. The invoice keeps the proforma's amounts; the difference is returned with a credit note.`
      : null,
  ].filter((w): w is string => w != null);

  return (
    <Box>
      <DocumentLayout
        header={
          <DocumentHeader
            number={f.number}
            action={<OpportunityButton group={group.data} />}
            stateLabel="Issued"
            stateColor="teal"
          />
        }
        aside={
          <>
            {group.data && <ChainStrip group={group.data} />}
            <FinalPaymentCard f={f} />
            <PartiesCard parties={f.parties} warnings={warnings} />
            <ActivityCard kind="final_invoice" id={f.id} />
          </>
        }
      >
        <Stack gap={8}>
          <OfferLine group={group.data} />
          <Stack gap={4}>
            <Group gap={6} wrap="wrap">
              {sendAction && (
                <ActionButton
                  label={sendAction.label}
                  reason={sendAction.reason}
                  hint={finalLastSent(f.deliveries)}
                  variant="filled"
                  icon={<PiPaperPlaneTilt size={12} />}
                  loading={send.isPending}
                  onClick={doSend}
                />
              )}
              <ActionButton
                label="PDF"
                reason={null}
                icon={<PiDownloadSimple size={12} />}
                loading={download.isPending}
                onClick={doDownload}
              />
            </Group>
            {f.pdf.state !== 'stored' && (
              <Text size="xs" c={f.pdf.state === 'failed' ? 'red' : 'dimmed'}>
                {f.pdf.state === 'failed'
                  ? `PDF failed: ${f.pdf.error}. Download tries again.`
                  : 'PDF is being prepared.'}
              </Text>
            )}
            {f.queuedSends.map((s) => (
              <Text key={s.id} size="xs" c="orange">
                Queued {day(s.createdAt)} to {s.recipientEmail}, sent when its
                PDF is stored.
              </Text>
            ))}
          </Stack>
        </Stack>
        <Paper withBorder p="md">
          <DocumentView kind="final_invoice" id={f.id} version={null} />
        </Paper>
      </DocumentLayout>
    </Box>
  );
}
